/*
  Marker classes replacing the :has() selectors this theme used to rely on.

  Why: Safari re-evaluates :has() far more eagerly than other engines, and
  document-rooted ones (body:has(...), .site-main:has(...)) get re-checked
  whenever the page changes or the viewport resizes. Opening the on-screen
  keyboard resizes the viewport, so every tap on a text field was stalling for
  roughly 2.8 seconds on iOS. Confirmed by serving the same stylesheet with only
  the :has() rules removed, which made the keyboard instant.

  Each condition is evaluated here once, in JavaScript, and recorded as a class.
  The CSS then matches a plain class, which costs nothing to re-check.

  Deliberately no document-wide MutationObserver: that is what caused a separate
  fault earlier in this theme. App-injected markup (Seal Subscriptions) is picked
  up by a small number of bounded retries and an observer scoped to that one
  container, which disconnects once it has what it needs.
*/
(function () {
  'use strict';

  if (window.__qvHasShims) return;
  window.__qvHasShims = true;

  var APP_BLOCK = '.product-block--\\@app';

  // .template-collection .shopify-section:has(.hg-tiles-section) + .shopify-section
  function markTileNeighbour() {
    var tiles = document.querySelector('.template-collection .hg-tiles-section');
    if (!tiles) return;
    var section = tiles.closest('.shopify-section');
    var next = section && section.nextElementSibling;
    if (next && next.classList && next.classList.contains('shopify-section')) {
      next.classList.add('qv-after-hg-tiles');
    }
  }

  // .custom-liquid--container:has(.qv-benefits)
  function markBenefitsWrapper() {
    var benefits = document.querySelector('.qv-benefits');
    if (!benefits) return;
    var wrap = benefits.closest('.custom-liquid--container');
    if (wrap) wrap.classList.add('qv-benefits-wrap');
  }

  // .tabbed-content.pxu-tabs:not(:has(.tab-label))
  function markEmptyTabSets() {
    var sets = document.querySelectorAll('.tabbed-content.pxu-tabs');
    for (var i = 0; i < sets.length; i++) {
      sets[i].classList.toggle('qv-tabs-empty', !sets[i].querySelector('.tab-label'));
    }
  }

  // .product-section--container:not(:has(*))
  function markEmptySections() {
    var sections = document.querySelectorAll('.product-section--container');
    for (var i = 0; i < sections.length; i++) {
      sections[i].classList.toggle('qv-section-empty', !sections[i].firstElementChild);
    }
  }

  // .product-details:has(.lipscore-rating:empty) and the app block around it.
  // Re-checked on the retries below because Lipscore fills the container late.
  function markReviewState() {
    var details = document.querySelector('.product-details');
    if (!details) return;
    var rating = details.querySelector('.lipscore-rating');
    if (!rating) return;
    var empty = rating.children.length === 0 && !rating.textContent.trim();
    details.classList.toggle('qv-no-reviews', empty);
    var block = rating.closest(APP_BLOCK);
    if (block) block.classList.toggle('qv-review-block-empty', empty);
  }

  // .sls-purchase-options-container > *:has(input[type="radio"]) and its
  // :not(:has(input)) counterpart. Seal exposes no stable per-row class, which
  // is why the original rules had to be structural.
  function markSubscriptionCards() {
    var box = document.querySelector('.sls-purchase-options-container');
    if (!box) return false;
    var kids = box.children;
    var found = false;
    for (var i = 0; i < kids.length; i++) {
      var kid = kids[i];
      if (kid.querySelector('input[type="radio"]')) {
        kid.classList.add('qv-sls-card');
        kid.classList.remove('qv-sls-nocard');
        found = true;
      } else if (!kid.querySelector('input')) {
        kid.classList.add('qv-sls-nocard');
      }
    }
    return found;
  }

  // The product block holding the Lipscore rating, so CSS can place it beside
  // the price on mobile. A marker class rather than :has() - see the note at
  // the top of theme.css.liquid for why this theme avoids :has() entirely.
  function markRatingBlock() {
    var rating = document.querySelector('.product-details .lipscore-rating');
    if (!rating || !rating.closest) return;
    var block = rating.closest('.product-block');
    if (block) block.classList.add('qv-rating-block');
  }

  // App blocks that render nothing still occupy ~9px each in the product
  // column. Marked so CSS can collapse them on mobile, where that dead space
  // sits between the benefit bullets and the add-to-cart row.
  //
  // Re-evaluated on every pass rather than marked once: Seal Subscriptions and
  // the review widgets fill their block after load, so a block that is empty at
  // DOMContentLoaded may not be empty a second later. toggle() puts the class
  // back off again as soon as content arrives.
  function markEmptyAppBlocks() {
    var blocks = document.querySelectorAll('.product-details > .product-block--\\@app');
    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      if (b.classList.contains('qv-rating-block')) continue;
      var empty = b.offsetHeight < 12 && b.textContent.trim() === '' &&
                  !b.querySelector('img, iframe, input, button, select');
      b.classList.toggle('qv-app-empty', empty);
    }
  }

  function pass() {
    markEmptyAppBlocks();
    markRatingBlock();
    markTileNeighbour();
    markBenefitsWrapper();
    markEmptyTabSets();
    markEmptySections();
    markReviewState();
    return markSubscriptionCards();
  }

  function start() {
    pass();
    // Bounded retries for markup that arrives after us - Lipscore ratings and
    // the subscription widget. Finite and cheap, unlike a standing observer.
    [300, 800, 1500, 3000, 6000].forEach(function (ms) { setTimeout(pass, ms); });

    // If the subscription container exists but is still empty, watch just that
    // one element until it fills, then stop.
    var box = document.querySelector('.sls-purchase-options-container');
    if (box && window.MutationObserver && !markSubscriptionCards()) {
      var mo = new MutationObserver(function () {
        if (markSubscriptionCards()) mo.disconnect();
      });
      mo.observe(box, { childList: true, subtree: true });
      setTimeout(function () { mo.disconnect(); }, 15000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();


/*
  qv-kbd-log

  Diagnostics for the mobile keyboard problem, OFF unless you ask for it.

  Three fixes for this have shipped blind and none was confirmed, because the
  fault only exists on a real iPhone and the evidence dies with the page when
  it navigates. This records what actually happens - into sessionStorage, so it
  survives the hop to the results page - and draws it on screen.

    turn on:   add ?qvkbd=1 to any URL (sticks for the browser tab)
    turn off:  add ?qvkbd=0
    clear:     tap the panel

  When on, it also wraps HTMLElement.focus() to record a stack trace for every
  programmatic focus of a text field. That is the one question we cannot answer
  by reading the theme: if something re-focuses the search box, this names it.
  None of that is installed when the flag is absent, so the cost on a normal
  page load is one sessionStorage read.
*/
(function () {
  'use strict';

  var KEY = 'qvkbd';
  var LOG = 'qvkbd:log';
  var on = false;

  window.__qvKbdLog = function () {};

  try {
    if (/[?&]qvkbd=1/.test(location.search)) sessionStorage.setItem(KEY, '1');
    if (/[?&]qvkbd=0/.test(location.search)) {
      sessionStorage.removeItem(KEY);
      sessionStorage.removeItem(LOG);
    }
    on = sessionStorage.getItem(KEY) === '1';
  } catch (err) {}

  if (!on) return;

  var lines = [];
  try { lines = JSON.parse(sessionStorage.getItem(LOG) || '[]'); } catch (err) {}

  var t0 = Date.now();
  var panel = null;

  function describe(el) {
    if (!el) return 'null';
    if (el === document.body) return 'body';
    var out = (el.tagName || '?').toLowerCase();
    if (el.name) out += '[' + el.name + ']';
    if (el.hasAttribute && el.hasAttribute('data-live-search-input')) out += '{search}';
    return out;
  }

  function log(msg) {
    var vv = window.visualViewport;
    var kb = vv ? Math.round(window.innerHeight - vv.height) : -1;
    lines.push(
      (Date.now() - t0) + 'ms ' + location.pathname.slice(0, 18) +
      ' | ' + msg + ' | af=' + describe(document.activeElement) + ' kb=' + kb
    );
    if (lines.length > 120) lines = lines.slice(-120);
    try { sessionStorage.setItem(LOG, JSON.stringify(lines)); } catch (err) {}
    render();
  }

  window.__qvKbdLog = log;

  function render() {
    if (!document.body) return;
    if (!panel) {
      panel = document.createElement('pre');
      panel.style.cssText = [
        'position:fixed', 'left:0', 'right:0', 'bottom:0', 'z-index:2147483647',
        'margin:0', 'padding:6px', 'max-height:42vh', 'overflow:auto',
        'background:rgba(0,0,0,.85)', 'color:#0f0', 'font:10px/1.35 ui-monospace,monospace',
        'white-space:pre-wrap', 'word-break:break-all'
      ].join(';');
      panel.addEventListener('click', function () {
        lines = [];
        try { sessionStorage.removeItem(LOG); } catch (err) {}
        panel.textContent = '(cleared)';
      });
      document.body.appendChild(panel);
    }
    panel.textContent = lines.join('\n');
  }

  // Name whatever focuses a text field without being asked.
  var nativeFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function () {
    var tag = this.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') {
      var stack = '';
      try { stack = (new Error().stack || '').split('\n').slice(1, 4).join(' <- '); } catch (err) {}
      log('focus() on ' + describe(this) + ' via ' + stack.replace(/https?:\/\/[^ )]*\//g, ''));
    }
    return nativeFocus.apply(this, arguments);
  };

  document.addEventListener('focusin', function (e) { log('focusin ' + describe(e.target)); }, true);
  document.addEventListener('focusout', function (e) { log('focusout ' + describe(e.target)); }, true);
  window.addEventListener('pageshow', function (e) { log('pageshow persisted=' + e.persisted); });
  window.addEventListener('load', function () { log('load'); });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', function () { log('vv resize'); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { log('domready'); });
  } else {
    log('domready(late)');
  }
})();


/*
  qv-filter-enter-dismiss

  THE ACTUAL BUG. /pages/brands has a client-side brand filter
  (#brand-filter-input). It is not inside a form and its only listener is
  'input' - so pressing Return does literally nothing, and on iOS the keyboard
  sits over the list the shopper has just filtered down to. Confirmed from a
  log on Dan's phone: focusin at 5596ms, keyboard up 71ms later, and it stayed
  up for the next 10.7 seconds until he tapped somewhere else.

  This is the page that was being reported all along. The earlier rounds of work
  were aimed at /search, which is a different page with a different form, and
  that is why none of them ever changed what he saw.

  On a live filter, Return means "I have finished typing, show me the list", so
  the only correct response is to drop focus. Deliberately no preventDefault and
  no stopPropagation: there is no default to cancel outside a form, and anything
  that ever listens on this field later should still receive the event.

  enterkeyhint makes the key read "done" rather than a "go" that goes nowhere.
  The data-qv-enter-dismiss hook is there so the next filter box like this one
  needs no JS change at all.
*/
(function () {
  'use strict';

  var SELECTOR = '#brand-filter-input, [data-qv-enter-dismiss]';

  function setHints() {
    var els = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) {
      if (!els[i].getAttribute('enterkeyhint')) {
        els[i].setAttribute('enterkeyhint', 'done');
      }
    }
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var el = e.target;
    if (!el || !el.matches || !el.matches(SELECTOR)) return;
    if (window.__qvKbdLog) window.__qvKbdLog('filter Enter -> blur');
    el.blur();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setHints);
  } else {
    setHints();
  }
})();


/*
  qv-search-enter-blur

  Mobile: leaving the search field left the on-screen keyboard up over the
  results page.

  What the theme actually does. LiveSearch itself only binds
  keyup/focus/cancel/filter/resize - but it builds a SearchForm, and SearchForm
  DOES bind submit. Its handler preventDefaults, clones the whole form into a
  hidden node, appends the clone to <body> and calls submit() on it. The
  original input is never blurred, so the theme's own path always navigates with
  the search field still focused, and iOS keeps a keyboard up for a focused
  field across a navigation. That is the fault.

  Two things the earlier attempts got wrong:

  1. Timing. They marked the field readonly, blurred it, then navigated after a
     flat 180ms. iOS takes roughly 250-300ms to run the keyboard dismissal
     animation, and starting a navigation part-way through abandons it. A bigger
     guess is still a guess, so this waits for the thing itself:
     visualViewport.height comes back up when the keyboard has actually gone.

  2. Coverage. They hooked keydown Enter only. But Enter is not the only way out
     of this form - the magnifier button is a real submit button, and iOS does
     not always produce a keydown for the on-screen Go key. Anything that did
     not come through keydown fell straight into the theme's handler, keyboard
     and all. So submit is intercepted too, and both paths share one exit.

  Search semantics are preserved exactly, including the theme's habit of folding
  a selected filter into the q parameter as "<filter> AND <terms>". This should
  change when the keyboard goes away, and nothing else.
*/
(function () {
  'use strict';

  // Below this the gap is browser chrome (URL bar insets), not a keyboard.
  var KEYBOARD_MIN_PX = 100;
  // Longest we will hold a shopper's search waiting for a dismissal event.
  var WAIT_CAP_MS = 800;

  function log(msg) { if (window.__qvKbdLog) window.__qvKbdLog(msg); }

  function keyboardUp() {
    var vv = window.visualViewport;
    if (!vv) return false;
    return (window.innerHeight - vv.height) > KEYBOARD_MIN_PX;
  }

  function whenKeyboardGone(done) {
    var vv = window.visualViewport;
    if (!vv || !keyboardUp()) { done('not-up'); return; }

    var fired = false;
    function finish(why) {
      if (fired) return;
      fired = true;
      vv.removeEventListener('resize', onResize);
      done(why);
    }
    function onResize() { if (!keyboardUp()) finish('vv'); }

    vv.addEventListener('resize', onResize);
    setTimeout(function () { finish('cap'); }, WAIT_CAP_MS);
  }

  function searchUrl(form, input) {
    var action = form.getAttribute('action') || '/search';
    var terms = (input.value || '').trim();

    // Mirror SearchForm.submitHandler: a selected filter is folded into q.
    var filter = form.querySelector('[data-live-search-filter]');
    if (filter && filter.value) terms = filter.value + ' AND ' + terms;

    var params;
    try {
      params = new URLSearchParams(new FormData(form));
      params.set('q', terms);
    } catch (err) {
      return action + '?q=' + encodeURIComponent(terms);
    }
    return action + '?' + params.toString();
  }

  // The single exit both paths use.
  function leave(form, input, how) {
    var url = searchUrl(form, input);
    log('leaving via ' + how);

    // readonly before blur: iOS will not raise a keyboard for a readonly field,
    // so nothing can bring it back between here and the navigation. The field
    // is about to be replaced by a fresh page anyway.
    try { input.setAttribute('readonly', 'readonly'); } catch (err) {}
    input.blur();
    if (document.activeElement && document.activeElement !== document.body &&
        document.activeElement.blur) {
      document.activeElement.blur();
    }

    whenKeyboardGone(function (why) {
      log('navigating (' + why + ')');
      window.location.href = url;
    });
  }

  function fieldOf(form) {
    return form && form.querySelector && form.querySelector('[data-live-search-input]');
  }

  // Path 1: Enter in the field. Capture phase + stopImmediatePropagation keeps
  // both SearchForm.submitHandler and LiveSearch's 250ms-debounced _search out
  // of it - that debounce used to fire after we had blurred, while the old page
  // was still alive, and re-opened the takeover.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target;
    if (!input || !input.matches || !input.matches('[data-live-search-input]')) return;
    if (!(input.value || '').trim()) return;

    var form = input.form || (input.closest && input.closest('form'));
    if (!form) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    leave(form, input, 'keydown');
  }, true);

  // Path 2: everything else - the magnifier button, and any implicit submission
  // that arrives without a keydown we recognised.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    var input = fieldOf(form);
    if (!input) return;
    if (!(input.value || '').trim()) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    leave(form, input, 'submit');
  }, true);
})();

/*
  qv-lipscore-defer

  Lipscore hydrates every .lipscore-rating-small container it finds, one HTTP
  request each. On a brand or collection page that was ~31 requests to
  wapi.lipscore.com fired before the shopper had scrolled - measured at 1.5s of
  the page load, and the single biggest third-party cost on the page. Cards now
  render with .qv-lipscore-defer and get the real class only when they are
  actually near the viewport.

  Deliberately measurement-based rather than IntersectionObserver-only. Two
  reasons: an un-hydrated rating box is empty and measures 0x0, and a zero-area
  target does not reliably report as intersecting; and IO callbacks can be
  throttled entirely in a backgrounded or non-painting tab, which would leave
  ratings permanently missing. A plain getBoundingClientRect sweep on scroll,
  resize and load always runs, so the worst case is a slightly later hydrate
  rather than none. IO is kept as a cheap accelerator where it does fire.

  Scores are unchanged - same widget, same data, fetched later. No height is
  reserved: 18 of 31 products on a typical page have no reviews and render
  nothing, so a min-height would add an empty gap under most cards.
*/
(function () {
  var SELECTOR = '.qv-lipscore-defer';
  var MARGIN = 600;
  var scheduled = false;
  var polling = false;

  function runInit() {
    if (window.lipscore && typeof window.lipscore.initWidgets === 'function') {
      window.lipscore.initWidgets();
      return true;
    }
    return false;
  }

  // The app embed loads async, so a card can come into view before it is ready.
  function pollForLipscore() {
    if (polling) return;
    polling = true;
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (runInit() || tries > 80) { clearInterval(timer); polling = false; }
    }, 250);
  }

  function scheduleInit() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      if (!runInit()) pollForLipscore();
      // 300ms, not 60ms: initWidgets() rescans the whole document and re-fetches
      // containers it has already done, so calling it once per newly-revealed
      // card doubled the request count. Coalescing a burst of reveals into one
      // call is what actually keeps the saving.
    }, 300);
  }

  function activate(el) {
    if (!el.classList.contains('qv-lipscore-defer')) return false;
    el.classList.remove('qv-lipscore-defer');
    el.classList.add('lipscore-rating-small');
    return true;
  }

  // Measure the CARD, never the rating box - the rating box is 0x0 until it
  // has been hydrated, so its own rect says nothing about where it is.
  function hostOf(el) {
    return (el.closest && el.closest('.productgrid--item')) || el.parentElement || el;
  }

  function sweep() {
    var els = document.querySelectorAll(SELECTOR);
    if (!els.length) return 0;
    var limit = (window.innerHeight || 0) + MARGIN;
    var hit = 0;
    Array.prototype.forEach.call(els, function (el) {
      var r = hostOf(el).getBoundingClientRect();
      if (r.top < limit && r.bottom > -MARGIN) { if (activate(el)) hit += 1; }
    });
    if (hit) scheduleInit();
    return hit;
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    // setTimeout, not requestAnimationFrame: rAF is starved in a tab that is
    // not painting, which is the same condition that silences
    // IntersectionObserver. A timer keeps the sweep running regardless.
    setTimeout(function () { ticking = false; sweep(); }, 100);
  }

  function start() {
    sweep();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', sweep);

    // Cheap accelerator: hydrates on the frame a card scrolls in, where it runs.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var any = false;
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          var list = e.target.__qvLs || [];
          list.forEach(function (el) { if (activate(el)) any = true; });
          e.target.__qvLs = null;
        });
        if (any) scheduleInit();
      }, { rootMargin: MARGIN + 'px 0px' });
      Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), function (el) {
        var host = hostOf(el);
        if (!host.__qvLs) { host.__qvLs = []; io.observe(host); }
        if (host.__qvLs.indexOf(el) === -1) host.__qvLs.push(el);
      });
    }

    // Filtering and pagination swap the grid out, bringing fresh cards with it.
    var grid = document.querySelector('[data-productgrid-items], .productgrid--items');
    if (grid && 'MutationObserver' in window) {
      var debounce;
      new MutationObserver(function () {
        clearTimeout(debounce);
        debounce = setTimeout(sweep, 120);
      }).observe(grid, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();


/*
  qv-drop-keyboard-on-arrival

  The other half of the keyboard problem, on the page being ARRIVED AT.

  The previous version blurred whatever was focused once, at DOMContentLoaded /
  load / pageshow. That is too early and too brief: anything that focuses the
  search field a moment later - an app embed, a restored bfcache page, Safari
  itself - simply wins, and the field ends up focused with the keyboard up.
  Which is what Dan sees: a cursor blinking in the results-page search box that
  he never tapped.

  So this does not race. It holds a rule for the first few seconds of a page:
  the search field may not take focus until the shopper has touched the page.
  Real taps are unaffected, because touchstart fires before focus - by the time
  the focus lands the interaction flag is already set, and the field is left
  alone. Anything focusing before any interaction at all did not come from the
  shopper, and gets blurred.

  Two deliberate limits, both to keep the blast radius at the reported fault:

    - the search field only, not every text input. A login or newsletter field
      that autofocuses is somebody's intended behaviour and none of our business.
    - touch devices only. There is no on-screen keyboard to fight anywhere else,
      and the standing scope for this work is mobile.
*/
(function () {
  'use strict';

  // Gated on touch, not on width: an iPhone in landscape is wider than the
  // mobile breakpoint but still has an on-screen keyboard, which is the only
  // thing this is here to fight. Desktop is excluded outright.
  if (!((navigator.maxTouchPoints || 0) > 0 || 'ontouchstart' in window)) return;

  var FIELD = '[data-live-search-input]';
  // Long enough to cover late app embeds, short enough that the page behaves
  // completely normally by the time anyone has read it.
  var GRACE_MS = 4000;

  var interacted = false;
  var since = Date.now();

  ['touchstart', 'pointerdown', 'mousedown', 'click', 'keydown'].forEach(function (type) {
    document.addEventListener(type, function () { interacted = true; }, true);
  });

  function unrequested() {
    return !interacted && (Date.now() - since) < GRACE_MS;
  }

  function reject(el) {
    if (!el || !el.blur || !el.matches || !el.matches(FIELD)) return;
    if (window.__qvKbdLog) window.__qvKbdLog('rejecting unrequested search focus');
    el.blur();
  }

  document.addEventListener('focusin', function (e) {
    if (unrequested()) reject(e.target);
  }, true);

  function sweep() { if (unrequested()) reject(document.activeElement); }

  // pageshow also covers the bfcache restore after a back gesture, where iOS
  // hands the page back with its old focus intact. Reset the window there.
  window.addEventListener('pageshow', function () {
    interacted = false;
    since = Date.now();
    sweep();
  });
  window.addEventListener('load', sweep);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();


/*
  qv-delivery-msg-sync

  The basket page has two free-delivery indicators: the progress bar in the
  sidebar (snippets/free-shipping-bar.liquid) and the line under the order total
  (sections/static-cart.liquid). Changing a quantity updated the bar but not the
  line, so the basket could show "Only £10.00 away from FREE UK delivery" and
  "You are eligible for free shipping" at the same time - which is what Dan hit
  with 2 x £30.00 against a £40 threshold.

  Why the line was stale: empire.js's cart updater re-renders the section and
  then swaps only the data-html keys it names in code - list, cart_total,
  discounts and free_shipping_bar. Any other data-html block is collected into
  the response and then dropped on the floor, so marking the line up as one
  would look right and still do nothing.

  What it does dispatch is a `cartcount:update` CustomEvent carrying Shopify's
  cart JSON, which has total_price in it (after discount codes) - the same figure
  the Liquid computes from. So the line is recomputed here from that, against the threshold
  and money format the Liquid hands over as attributes. No second network call,
  and no hardcoded £40 to drift out of step with the theme setting.

  Total, not subtotal (changed 22 Oct 2026): Shopify's free-delivery rate is judged
  after discount codes, so items_subtotal_price told customers delivery was free
  when checkout then charged £3.99.
*/
(function () {
  'use strict';

  // Mirrors Shopify's money_format tokens. Anything unrecognised falls through
  // to the raw amount rather than printing a broken string.
  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return String(format || '{{amount}}').replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      function (whole, token) {
        switch (token) {
          case 'amount': return withCommas + '.' + parts[1];
          case 'amount_no_decimals': return withCommas;
          case 'amount_with_comma_separator': return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1];
          case 'amount_no_decimals_with_comma_separator': return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          case 'amount_with_apostrophe_separator': return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'") + '.' + parts[1];
          default: return whole;
        }
      }
    );
  }

  function sync(cart) {
    var el = document.querySelector('[data-qv-delivery-msg]');
    if (!el || !cart) return;

    var minimum = parseInt(el.getAttribute('data-qv-fs-min'), 10);
    var subtotal = cart.total_price;  // after discount codes - what Shopify's shipping rate uses (22 Oct 2026)
    if (isNaN(minimum) || typeof subtotal !== 'number') return;

    var remainder = minimum - subtotal;
    var free = remainder <= 0;

    el.classList.toggle('qv-delivery-msg--free', free);
    el.classList.toggle('qv-delivery-msg--short', !free);
    el.textContent = free
      ? 'Free UK delivery on this order'
      : 'Only ' + formatMoney(remainder, el.getAttribute('data-qv-money-format')) +
        ' away from FREE UK delivery';
  }

  // Fired by empire.js's cart updater with the cart JSON as detail, and by the
  // add-to-cart flyout. Both are moments when this line can go stale.
  window.addEventListener('cartcount:update', function (e) {
    sync(e && e.detail);
  });
})();

/*
  Price filter on mobile: drop the keyboard before the page reloads.

  empire.js binds BOTH keyup and change on [data-filter-range] to a 2000ms
  debounce, and the debounce ends in `window.location.search = ...` - a full
  page load, not an AJAX refresh. Nothing anywhere blurs the field. On a phone
  that means you type a price, the keyboard stays over the screen, and for two
  seconds nothing appears to happen; the reload then arrives from behind the
  keyboard. It reads as though the filter is broken.

  So we mirror the theme's debounce slightly early and blur, which closes the
  keyboard and lets the shopper actually watch the results change.

  Two things worth knowing if this ever needs touching:

  - 1700ms is deliberately just under empire.js's 2000ms. Both timers restart
    on every keystroke, so they stay in step. If the theme's debounce is ever
    changed, this needs changing with it - keep it comfortably below.
  - We only blur if that same field is STILL focused. Without that check,
    tabbing From -> To would blur the field the shopper just moved into.
    Blurring an already-unfocused element is a no-op, so the `change` handler
    is safe for the same reason.

  Scoped to coarse pointers. On desktop there is no keyboard to dismiss and
  yanking focus out of a field someone is still looking at would be worse than
  the problem.
*/
(function () {
  if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return;

  var THEME_DEBOUNCE_MS = 2000;
  var BLUR_AFTER_MS = 1700;
  var timers = new WeakMap();

  function isPriceField(el) {
    return el && el.matches && el.matches('[data-filter-range]');
  }

  function blurIfStillFocused(el) {
    if (document.activeElement === el) el.blur();
  }

  document.addEventListener('keyup', function (e) {
    var el = e.target;
    if (!isPriceField(el)) return;
    clearTimeout(timers.get(el));
    timers.set(el, setTimeout(function () { blurIfStillFocused(el); }, BLUR_AFTER_MS));
  }, true);

  // The keyboard's Go/Done key and tapping elsewhere both fire change. Nothing
  // to wait for in that case - the shopper has already told us they are done.
  document.addEventListener('change', function (e) {
    if (!isPriceField(e.target)) return;
    clearTimeout(timers.get(e.target));
    blurIfStillFocused(e.target);
  }, true);

})();

/*
  Price filter: pressing Return must not throw away the other active filters.

  The price form has no action and no method and nothing calls preventDefault,
  so Return triggers a NATIVE form submit. A form like that GETs the current
  path with only its OWN fields, replacing the whole query string. So:

      before Return   ?filter.p.vendor=Biocare&filter.v.price.gte=4
      after Return    ?filter.v.price.gte=4&filter.v.price.lte=

  ...and the shopper's brand filter is silently gone. They get a different
  result set from the one they asked for, with nothing to indicate why.

  empire.js's own JS path (_buildFilterQuery) merges properly - it is only the
  Return key that bypasses it, because that is the browser submitting the form
  rather than the theme handling an event. So we take Return over: cancel the
  native submit and navigate to the merged query instead.

  NOT scoped to mobile. Return does the same thing on a desktop keyboard.

  Two deliberate differences from the theme's merge:
  - Empty boxes are dropped rather than sent as `filter.v.price.lte=`. That
    also means clearing a box correctly REMOVES that half of the range, which
    sending an empty value does not reliably do.
  - `page` is reset. Filtering from page 4 of a wider result set otherwise
    lands you on page 4 of a narrower one, which can be empty. The theme has
    the same flaw on its own path; this at least fixes the Return route.
*/
(function () {
  var PRICE_PREFIX = 'filter.v.price';

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var el = e.target;
    if (!el || !el.matches || !el.matches('[data-filter-range]')) return;
    var form = el.closest('form');
    if (!form) return;

    // Stop the browser submitting the bare form.
    e.preventDefault();

    // Mobile: drop the keyboard and close the filter panel, so the collection
    // is what is on screen while the new page loads.
    if (document.activeElement === el) el.blur();
    var modal = document.querySelector('[data-modal-container]');
    if (modal && getComputedStyle(modal).display !== 'none') {
      var close = modal.querySelector('[data-modal-close]');
      if (close) close.click();
    }

    // Keep every existing parameter except the price pair and the page number.
    var params = new URLSearchParams(window.location.search);
    // Array.from, NOT Array.prototype.slice.call - a URLSearchParams iterator
    // has no length, so slice returns [] and the loop silently does nothing.
    // That failure hides itself: with no price param present there is nothing
    // to remove, and the append below still works, so it looks correct until
    // someone edits an existing range and gets a duplicated parameter.
    Array.from(params.keys()).forEach(function (k) {
      if (k.indexOf(PRICE_PREFIX) === 0 || k === 'page') params.delete(k);
    });

    // Re-add whichever price boxes actually have a value.
    new FormData(form).forEach(function (value, key) {
      if (String(value).trim() !== '') params.append(key, value);
    });

    var qs = params.toString();
    window.location.search = qs ? '?' + qs : '';
  }, true);
})();
